import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {useAuth} from '../../../../auth'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import SettingsService from '../../../core/_requests'
import {toAbsoluteUrl} from '../../../../../../_gori/helpers'
import {Button} from '../../../../../../_gori/partials/widgets'
import {toast} from 'react-toastify'
import {OrderType} from '../../../../orders/core/_const'
import {ROUTES} from '../../../../../../_gori/constants'
import {DisconnectStoreModal} from '../../../modals/store/connections/DisconnectStoreModal'
import {api2CartConnectionConfigs} from './api2cart/config'

type StoreConnectionCardProps = {
  logoAlt: string
  logoSrc: string
  promoMarket?: string
  action: React.ReactNode
}

const ConnectionsPage: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {loadingSwitch} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()
  const [connections, setConnections] = useState<any>()
  const [isLoadingConnections, setIsLoadingConnections] = useState<boolean>(false)
  const [isLoadingShipStationConnection, setIsLoadingShipStationConnection] =
    useState<boolean>(false)
  const [store, setStore] = useState<string | null>(null)
  const [showModal, setShowModal] = useState<{disconnectStore: boolean}>({
    disconnectStore: false,
  })

  const getConnections = useCallback(async () => {
    const config = {
      cancelToken: newCancelToken(),
    }
    setIsLoadingConnections(true)
    await SettingsService.connections(config)
      .then((response) => {
        setConnections(response.connections)
      })
      .catch((error) => {
        if (isCancel(error)) return
      })
      .finally(() => {
        setIsLoadingConnections(false)
      })
  }, [isCancel, newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return

    getConnections()

    return () => {
      setConnections(undefined)
    }
  }, [loadingSwitch, getConnections])

  const handleDisconnectConfirmationModalClose = () => {
    setStore(null)
    setShowModal((prev) => ({...prev, disconnectStore: false}))
  }

  const handleShipStationDisconnect = useCallback(
    async (store_type) => {
      const config = {
        params: {
          store_type: store_type,
        },
        cancelToken: newCancelToken(),
      }

      handleDisconnectConfirmationModalClose()
      setIsLoadingShipStationConnection(true)

      await SettingsService.disconnect(config)
        .then(async () => {
          await getConnections()
          navigate(ROUTES.SETTINGS.CONNECTIONS.BASE)
          toast.success(intl.formatMessage({id: 'DISCONNECTED_SUCCESSFULLY'}))
        })
        .catch((error) => {
          if (isCancel(error)) return
        })
        .finally(() => {
          setIsLoadingShipStationConnection(false)
        })
    },
    [getConnections, intl, isCancel, navigate, newCancelToken]
  )

  const renderPromoTag = (market: string) => {
    return null
  }

  const renderStoreConnectionCard = ({
    logoAlt,
    logoSrc,
    promoMarket,
    action,
  }: StoreConnectionCardProps) => {
    return (
      <div className='store-connection-card'>
        <div className='card shadow-sm store-connection-card__card position-relative'>
          {promoMarket ? renderPromoTag(promoMarket) : null}
          <div className='card-body d-flex flex-column h-100'>
            <div className='store-connection-card__content'>
              <img
                alt={logoAlt}
                src={toAbsoluteUrl(logoSrc)}
                className='store-connection-card__logo'
                // className={'store-connection-logo mb-13'}
              />
              <div className='store-connection-card__action min-w-175px'>{action}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const managementCards = useMemo(
    () => [
      {
        key: OrderType.SHOPIFY,
        label: 'Shopify',
        promoMarket: 'shopify',
        logoSrc: '/media/gori/settings/store-connections/shopify-logo.svg',
        route: ROUTES.SETTINGS.CONNECTIONS.SHOPIFY,
      },
      {
        key: OrderType.WOOCOMMERCE,
        label: 'WooCommerce',
        promoMarket: api2CartConnectionConfigs[OrderType.WOOCOMMERCE].promoMarket,
        logoSrc: api2CartConnectionConfigs[OrderType.WOOCOMMERCE].logoPath,
        route: api2CartConnectionConfigs[OrderType.WOOCOMMERCE].route,
      },
      {
        key: OrderType.MAGENTO2,
        label: 'Magento',
        promoMarket: api2CartConnectionConfigs[OrderType.MAGENTO2].promoMarket,
        logoSrc: api2CartConnectionConfigs[OrderType.MAGENTO2].logoPath,
        route: api2CartConnectionConfigs[OrderType.MAGENTO2].route,
      },
      {
        key: OrderType.WALMART,
        label: 'Walmart',
        promoMarket: 'walmart',
        logoSrc: '/media/gori/settings/store-connections/walmart-logo.svg',
        route: ROUTES.SETTINGS.CONNECTIONS.WALMART,
      },
      {
        key: OrderType.TIKTOK,
        label: 'TikTok',
        promoMarket: 'tiktok',
        logoSrc: '/media/gori/settings/store-connections/tiktok-logo.svg',
        route: ROUTES.SETTINGS.CONNECTIONS.TIKTOK,
      },
      {
        key: OrderType.EBAY,
        label: 'eBay',
        promoMarket: 'ebay',
        logoSrc: '/media/gori/settings/store-connections/ebay-logo.png',
        route: ROUTES.SETTINGS.CONNECTIONS.EBAY,
      },
      {
        key: OrderType.TEMU,
        label: 'Temu',
        promoMarket: 'temu',
        logoSrc: '/media/gori/settings/store-connections/temu-logo.svg',
        route: ROUTES.SETTINGS.CONNECTIONS.TEMU,
      },
    ],
    []
  )

  const renderManageCard = (card: {
    key: string
    label: string
    promoMarket?: string
    logoSrc: string
    route: string
  }) => {
    return renderStoreConnectionCard({
      logoAlt: card.key,
      logoSrc: card.logoSrc,
      promoMarket: card.promoMarket,
      action: (
        <Button
          className='btn btn-sm btn-advanced-primary min-w-175px fs-sm-5 text-nowrap'
          label={intl.formatMessage({id: 'MANAGE_CONNECTIONS'})}
          loadingText={intl.formatMessage({id: 'MANAGE_CONNECTIONS'})}
          loading={isLoadingConnections}
          disabled={isLoadingConnections}
          event={() => navigate(card.route)}
        />
      ),
    })
  }

  return (
    <>
      {showModal.disconnectStore && store && (
        <DisconnectStoreModal
          show={showModal.disconnectStore}
          handleClose={handleDisconnectConfirmationModalClose}
          handleDisconnectStore={() => {
            handleShipStationDisconnect(OrderType.SHIPSTATION)
          }}
          storeName={store}
        />
      )}
      <div className='d-flex flex-column align-items-center justify-content-center'>
        <div className='d-flex justify-content-start w-100 settings-header table-settings-header'>
          <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
            {intl.formatMessage({id: 'STORE_CONNECTIONS'})}
          </h1>
        </div>
        <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
          <div className='store-connections-cards settings-card'>
            {renderStoreConnectionCard({
              logoAlt: 'shipstation',
              logoSrc: '/media/gori/settings/store-connections/shipstation-logo.png',
              action: (
                <Button
                  className={`btn btn-sm fs-sm-5 min-w-175px text-nowrap ${
                    connections?.shipstation?.length > 0 ? 'btn-danger' : 'btn-advanced-primary'
                  }`}
                  label={intl.formatMessage({
                    id: connections?.shipstation?.length > 0 ? 'DISCONNECT' : 'CONNECT',
                  })}
                  loadingText={intl.formatMessage({
                    id: connections?.shipstation?.length > 0 ? 'DISCONNECT' : 'CONNECT',
                  })}
                  loading={isLoadingConnections || isLoadingShipStationConnection}
                  disabled={isLoadingConnections || isLoadingShipStationConnection}
                  event={(e) => {
                    e.currentTarget.blur()
                    if (connections?.shipstation?.length > 0) {
                      setStore('ShipStation')
                      setShowModal((prev) => ({...prev, disconnectStore: true}))
                    } else {
                      navigate(ROUTES.SETTINGS.CONNECTIONS.SHIPSTATION)
                    }
                  }}
                />
              ),
            })}

            {managementCards.map(renderManageCard)}

            {renderStoreConnectionCard({
              logoAlt: 'amazon',
              logoSrc: '/media/gori/settings/store-connections/amazon-logo-lock.png',
              action: (
                <p className='store-connection-card__coming-soon'>
                  {intl.formatMessage({id: 'COMING_SOON'})}
                </p>
              ),
            })}
          </div>
        </CSSTransition>
      </div>
    </>
  )
}

export {ConnectionsPage}
